// Imports
import { loadStripe } from '@stripe/stripe-js/pure';
import { useMemo } from 'react';
import thirdPartyIdentifiers from '../../../../shareables/third-party-identifiers';
import isScreenshot from '../../../../shareables/utils/is-screenshot';
import commonActions from '../../../foundation/front-end/redux/actions';
import store from '../../../foundation/front-end/redux/store';


// Initialize
const loadFailureMessage =
	'Unable to connect to our payment processor. If you’re using an ad blocker or privacy extension, try disabling it for this website and then refresh the page.';


// A hook that handles loading a Stripe instance
const useStripeInstance = (stripeAccountID?: string) => {
	// Verify publishable keys exist
	if (!thirdPartyIdentifiers.stripePublishableKeys) {
		throw new Error('Missing required Stripe publishable keys');
	}
	
	
	// Memoize Stripe
	const publishableKey =
		thirdPartyIdentifiers.stripePublishableKeys[process.env.REACT_APP__ENVIRONMENT === 'production' ? 'live' : 'test'];
	
	const stripe = useMemo(() => {
		if (isScreenshot()) {
			return Promise.resolve(null);
		}
		
		return loadStripe(publishableKey, {
			...(stripeAccountID
				? {
						stripeAccount: stripeAccountID,
					}
				: {}),
		}).catch((error: unknown) => {
			// Rethrow anything other than the script being blocked or failing to load
			if (!(error instanceof Error) || error.message !== 'Failed to load Stripe.js') {
				throw error;
			}
			
			
			// Show the message unless it's already showing
			const alreadyShowing = store
				.getState()
				.common.pageMessages.some(
					(pageMessage) => !pageMessage.dismissed && pageMessage.message === loadFailureMessage
				);
			
			if (!alreadyShowing) {
				store.dispatch(
					commonActions.showPageMessage({
						color: 'danger',
						title: 'Oh snap!',
						message: loadFailureMessage,
					})
				);
			}
			
			return null;
		});
	}, [stripeAccountID, publishableKey]);
	
	
	// Return Stripe promise
	return stripe;
};

export default useStripeInstance;
